import { inject, Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { PlatformUtilsService } from "@bitwarden/common/abstractions/platformUtils.service";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import {
  checkAppUpdate,
  openAppStore,
} from "@bitwarden/web-vault/app/capacitor-plugins/app-update";

const DISMISSED_VERSION_STORAGE_KEY = "appUpdateDismissedVersion";

@Injectable({
  providedIn: "root",
})
export class AppUpdateService {
  private platformUtilsService = inject(PlatformUtilsService);
  private confirmationDialogService = inject(ConfirmationDialogService);
  private i18nService = inject(I18nService);

  async checkForUpdate(): Promise<void> {
    if (!this.platformUtilsService.isMobileDevice()) {
      return;
    }

    const { available, version } = await checkAppUpdate();
    if (!available) {
      return;
    }

    if (version && localStorage.getItem(DISMISSED_VERSION_STORAGE_KEY) === version) {
      return;
    }

    const dialogRef = await this.confirmationDialogService.ctaNotification({
      title: this.i18nService.t("appUpdateAvailableTitle"),
      message: this.i18nService.t("appUpdateAvailableMessage"),
      cancel: {
        title: this.i18nService.t("maybeLater"),
        // Dismissal is recorded uniformly below, once the dialog actually closes — the X
        // button (CtaNotificationComponent.closeDialog()) closes without ever calling this
        // method, so recording it here alone missed that path (#3537 follow-up).
        method: () => {},
      },
      confirm: {
        title: this.i18nService.t("updateNow"),
        method: () => {
          openAppStore().catch((e) => {
            // eslint-disable-next-line no-console
            console.error("[AppUpdate] openAppStore failed", e);
          });
        },
      },
      reminder: "",
    });

    // "Maybe later" and the X both close the dialog with the same { result: "cancel" } shape
    // (see CtaNotificationComponent) — only that counts as a dismissal. A programmatic close
    // (e.g. MatDialog.closeAll() on lock/logout) yields `undefined` and must not record one.
    // Use beforeClosed() rather than afterClosed(): afterClosed() only resolves once
    // the exit animation finishes (MatDialogRef.close() defers it via _finishDialogClose()), so
    // backgrounding/killing the app in that ~200ms window would drop the dismissal. beforeClosed()
    // emits as soon as the close is requested, before the animation starts.
    const result = await firstValueFrom(dialogRef.beforeClosed());
    if (result?.result === "cancel" && version) {
      localStorage.setItem(DISMISSED_VERSION_STORAGE_KEY, version);
    }
  }
}
